"use client";

import Link from "next/link";
import { useActionState } from "react";
import { signIn, signUp, type AuthResult } from "@/lib/actions/auth";
import { Spinner } from "@/components/Spinner";

export function AuthForm({
  mode,
  next = "/library",
}: {
  mode: "signin" | "signup";
  next?: string;
}) {
  const signup = mode === "signup";
  const action = signup ? signUp : signIn;
  const [state, formAction, pending] = useActionState<AuthResult | null, FormData>(action, null);

  const needsConfirm = state && "ok" in state && state.needsConfirm;

  return (
    <div className="rounded-[16px] border border-border bg-surface p-8">
      <h1 className="font-serif text-[28px] font-semibold tracking-tight text-ink">
        {signup ? "Create your account" : "Welcome back"}
      </h1>
      <p className="mt-2 text-[14.5px] text-muted">
        {signup
          ? "Start building rule books in minutes — free."
          : "Sign in to your shelves, drafts, and reading progress."}
      </p>

      {needsConfirm ? (
        <div className="mt-6 rounded-[12px] border border-border bg-surface-alt p-5 text-[14px] leading-relaxed text-ink-3">
          Check your email to confirm your account, then{" "}
          <Link href={`/signin?next=${encodeURIComponent(next)}`} className="font-semibold text-copper">
            sign in
          </Link>
          .
        </div>
      ) : (
        <form action={formAction} className="mt-6 flex flex-col gap-3.5">
          <input type="hidden" name="next" value={next} />
          {signup && (
            <Field label="Name">
              <input name="name" required autoComplete="name" className="rb-input" placeholder="Your name" />
            </Field>
          )}
          <Field label="Email">
            <input name="email" type="email" required autoComplete="email" className="rb-input" placeholder="you@example.com" />
          </Field>
          <Field label="Password">
            <input
              name="password"
              type="password"
              required
              minLength={6}
              autoComplete={signup ? "new-password" : "current-password"}
              className="rb-input"
              placeholder="••••••••"
            />
          </Field>

          {state && "error" in state && (
            <p className="text-[13px] font-medium text-copper">
              {state.error}
              {state.code === "exists" && (
                <>
                  {" "}
                  <Link href={`/signin?next=${encodeURIComponent(next)}`} className="font-semibold underline">
                    Sign in instead
                  </Link>
                  .
                </>
              )}
            </p>
          )}

          <button
            type="submit"
            disabled={pending}
            aria-busy={pending}
            className="mt-2 inline-flex h-[48px] items-center justify-center gap-2.5 rounded-[12px] bg-green text-[15px] font-bold text-surface disabled:opacity-60"
            style={{ boxShadow: "var(--shadow-cta)" }}
          >
            {pending && <Spinner size={15} />}
            {pending
              ? signup
                ? "Creating account…"
                : "Signing in…"
              : signup
                ? "Create account"
                : "Sign in"}
          </button>
        </form>
      )}

      <p className="mt-6 text-center text-[14px] text-muted">
        {signup ? "Already have an account? " : "New to Rulebound? "}
        <Link
          href={signup ? `/signin?next=${encodeURIComponent(next)}` : `/signup?next=${encodeURIComponent(next)}`}
          className="font-semibold text-copper"
        >
          {signup ? "Sign in" : "Create one"}
        </Link>
      </p>

      <style>{`
        .rb-input {
          width: 100%;
          border: 1px solid var(--border);
          background: var(--paper);
          border-radius: 12px;
          padding: 12px 14px;
          font-size: 15px;
          color: var(--ink);
          outline: none;
        }
        .rb-input:focus { outline: 2px solid var(--copper); outline-offset: 2px; }
        .rb-input::placeholder { color: var(--muted-2); }
      `}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[12.5px] font-semibold text-ink-3">{label}</span>
      {children}
    </label>
  );
}
